import type { ReactElement } from 'react';
import { Icon, type IconProps } from '@pixum/icon';
import { AustrianFlag } from '@pixum/svg-icon/lib/Flags/AustrianFlag';
import { BelgianFlag } from '@pixum/svg-icon/lib/Flags/BelgianFlag';
import { BulgarianFlag } from '@pixum/svg-icon/lib/Flags/BulgarianFlag';
import { CzechFlag } from '@pixum/svg-icon/lib/Flags/CzechFlag';
import { DanishFlag } from '@pixum/svg-icon/lib/Flags/DanishFlag';
import { EnglishFlag } from '@pixum/svg-icon/lib/Flags/EnglishFlag';
import { FinnishFlag } from '@pixum/svg-icon/lib/Flags/FinnishFlag';
import { FrenchFlag } from '@pixum/svg-icon/lib/Flags/FrenchFlag';
import { GermanFlag } from '@pixum/svg-icon/lib/Flags/GermanFlag';
import { HungarianFlag } from '@pixum/svg-icon/lib/Flags/HungarianFlag';
import { IrishFlag } from '@pixum/svg-icon/lib/Flags/IrishFlag';
import { ItalianFlag } from '@pixum/svg-icon/lib/Flags/ItalianFlag';
import { LuxembourgFlag } from '@pixum/svg-icon/lib/Flags/LuxembourgFlag';
import { NetherlandsFlag } from '@pixum/svg-icon/lib/Flags/NetherlandsFlag';
import { PolishFlag } from '@pixum/svg-icon/lib/Flags/PolishFlag';
import { PortugueseFlag } from '@pixum/svg-icon/lib/Flags/PortugueseFlag';
import { SpanishFlag } from '@pixum/svg-icon/lib/Flags/SpanishFlag';
import { SwedishFlag } from '@pixum/svg-icon/lib/Flags/SwedishFlag';
import { SwissFlag } from '@pixum/svg-icon/lib/Flags/SwissFlag';

/**
 * Returns a React icon component based on a given name string.
 *
 * This function maps predefined icon names to their corresponding React components
 * and applies an optional color prop where supported. If the icon name is not found
 * in the map, it returns `undefined`.
 *
 * Supported icons:
 * - Standard icons: 'icon-clear', 'icon-check', 'icon-check-circle', 'icon-bell', 'icon-cart', 'icon-shield'
 * - Country flags by ISO code: 'flag-AT', 'flag-BE', 'flag-BG', 'flag-CH', 'flag-CZ',
 *   'flag-DE', 'flag-DK', 'flag-ES', 'flag-FI', 'flag-FR', 'flag-GB', 'flag-HU',
 *   'flag-IE', 'flag-IT', 'flag-LU', 'flag-NL', 'flag-PL', 'flag-PT', 'flag-SE'
 *
 * @param name - The name of the icon to render (e.g., 'icon-check', 'flag-DE').
 * @param {IconProps['color']} [color='white'] - The color to apply to the icon, if applicable.
 * @param {IconProps['size']} [size] - The size of the icon ('small', 'medium', or 'large').
 * @returns  A React icon element if the name exists, otherwise `undefined`.
 *
 */
export function getIconByName(
  name: string,
  color: string | IconProps['color'] = 'white',
  size?: IconProps['size'],
): ReactElement | undefined {
  const iconMap: Record<string, (props?: Partial<IconProps>) => ReactElement> = {
    'icon-clear': () => <Icon name="close" color={color} size={size} />,
    'icon-check-circle': () => <Icon name="check-circle" color={color} size={size} />,
    'icon-check': () => <Icon name="check" color={color} size={size} />,
    'icon-bell': () => <Icon name="notifications" color={color} size={size} />,
    'icon-cart': () => <Icon name="cart" color={color} size={size} />,
    'icon-shield': () => <Icon name="shield" color={color} size={size} />,
    // Country flags by ISO code (e.g. getIconByName('flag-DE'))
    'flag-AT': () => <AustrianFlag />,
    'flag-BE': () => <BelgianFlag />,
    'flag-BG': () => <BulgarianFlag />,
    'flag-CH': () => <SwissFlag />,
    'flag-CZ': () => <CzechFlag />,
    'flag-DE': () => <GermanFlag />,
    'flag-DK': () => <DanishFlag />,
    'flag-ES': () => <SpanishFlag />,
    'flag-FI': () => <FinnishFlag />,
    'flag-FR': () => <FrenchFlag />,
    'flag-GB': () => <EnglishFlag />,
    'flag-HU': () => <HungarianFlag />,
    'flag-IE': () => <IrishFlag />,
    'flag-IT': () => <ItalianFlag />,
    'flag-LU': () => <LuxembourgFlag />,
    'flag-NL': () => <NetherlandsFlag />,
    'flag-PL': () => <PolishFlag />,
    'flag-PT': () => <PortugueseFlag />,
    'flag-SE': () => <SwedishFlag />,
  };

  const renderIcon = iconMap[name];

  return renderIcon ? renderIcon() : undefined;
}
